import React, { useEffect, useMemo, useState } from "react";
import { openPopup } from "../Common/popupUIStore";
import StudentItem from "../Common/StudentItem";
import SearchToggle from "../Common/SearchToggle";

const firstLetter = (v) =>
   String(v || "")
      .trim()
      .charAt(0) || "";

function getInitials(student) {
   const fn = String(student?.firstName || "").trim();
   const ln = String(student?.lastName || "").trim();

   const a = firstLetter(fn);
   const b = firstLetter(ln);
   if (a && b) return (a + b).toUpperCase();

   const two = fn.slice(0, 2);
   if (two) return two.toUpperCase();

   return "–";
}

function hashStringToUInt(str) {
   let h = 0;
   for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
   return h >>> 0;
}

const AVATAR_HUES = [
   { h: 70, s: 75 },
   { h: 0, s: 100 },
   { h: 30, s: 100 },
   { h: 54, s: 95 },
   { h: 130, s: 65 },
   { h: 210, s: 90 },
   { h: 255, s: 98 },
   { h: 285, s: 100 },
   { h: 330, s: 96 },
];

const AVATAR_LIGHTNESSES = [94, 92, 90, 88, 86, 84, 82, 80, 78, 76, 74];

const AVATAR_COLORS = AVATAR_HUES.flatMap(({ h, s }) =>
   AVATAR_LIGHTNESSES.map((l) => `hsl(${h} ${s}% ${l}%)`),
);

function getRandomAvatarColor() {
   return AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
}

function getAvatarColorFromName(student) {
   const fullName =
      `${student?.firstName || ""} ${student?.lastName || ""}`.trim();
   const hasLetter = /\p{L}/u.test(fullName);
   if (!hasLetter) return null;

   const normalized = fullName.normalize("NFKD");
   const idx = hashStringToUInt(normalized) % AVATAR_COLORS.length;
   return AVATAR_COLORS[idx];
}

export default function ProfessorStudentsSection({
   students,
   groupNameById,
   loadingAll,
   errorAll,
   status,
   batchSize = 16,
   onVisibleStudentIdsChange,
   title = "Studentii mei",
   emptyHint = "Nu esti asignat la nicio grupa sau nu ai studenti in grupe.",
}) {
   const [search, setSearch] = useState({
      open: true,
      query: "",
   });
   const [visibleCount, setVisibleCount] = useState(batchSize);

   const filtered = useMemo(() => {
      const q = search.query.trim().toLowerCase();
      const base = Array.isArray(students) ? students : [];

      if (!q) return base;

      return base.filter((s) => {
         const gName = groupNameById?.[String(s?.groupId)] || "";
         return `${s.firstName || ""} ${s.lastName || ""} ${s.email || ""} ${s.phone || ""} ${gName}`
            .toLowerCase()
            .includes(q);
      });
   }, [students, search.query, groupNameById]);

   const visibleStudents = useMemo(
      () => filtered.slice(0, visibleCount),
      [filtered, visibleCount],
   );

   useEffect(() => {
      setVisibleCount(batchSize);
   }, [batchSize, students]);

   useEffect(() => {
      setVisibleCount(batchSize);
   }, [batchSize, search.query]);

   useEffect(() => {
      if (!onVisibleStudentIdsChange) return;
      onVisibleStudentIdsChange(
         visibleStudents
            .map((student) => String(student?.id || "").trim())
            .filter(Boolean),
      );
   }, [onVisibleStudentIdsChange, visibleStudents]);

   const avatarColorByKey = useMemo(() => {
      const m = new Map();

      filtered.forEach((student, idx) => {
         const key = String(
            student?.id ?? student?.phone ?? student?.email ?? `__idx_${idx}`,
         );
         const deterministic = getAvatarColorFromName(student);
         m.set(key, deterministic || getRandomAvatarColor());
      });

      return m;
   }, [filtered]);

   const handleToggleSearch = () => {
      setSearch((prev) =>
         prev.open ? { open: false, query: "" } : { ...prev, open: true },
      );
   };

   const handleOpenStudentPopup = (student) => {
      openPopup("instructorStudents", {
         initialStudent: student,
      });
   };

   const showNoneHint =
      status === "none" &&
      !loadingAll &&
      !errorAll &&
      (students?.length ?? 0) === 0;

   const totalResults = filtered.length;
   const canLoadMore = visibleCount < totalResults;
   const remaining = Math.max(0, totalResults - visibleCount);
   const nextBatch = Math.min(batchSize, remaining);

   return (
      <div className="studentsUI">
         <div className={`studentsUI__header ${search.open ? "is-open" : ""}`}>
            <h2 className="studentsUI__title">{title}</h2>

            <div className="studentsUI__right">
               <SearchToggle
                  open={search.open}
                  value={search.query}
                  onValueChange={(val) =>
                     setSearch((prev) => ({ ...prev, query: val }))
                  }
                  onToggle={handleToggleSearch}
                  placeholder="Cauta student..."
                  wrapperClassName="studentsUI__search"
                  inputClassName="studentsUI__input"
                  buttonClassName="studentsUI__iconBtn"
                  iconClassName={`studentsUI__icon ${search.open ? "is-rotated" : ""}`}
                  titleOpen="Inchide cautarea"
                  titleClosed="Cauta"
               />
            </div>
         </div>

         {showNoneHint && (
            <div style={{ padding: "10px 14px", opacity: 0.85 }}>
               {emptyHint}
            </div>
         )}

         <div className="studentsUI__gridWrap">
            <div className="studentsUI__grid">
               {loadingAll && (
                  <p style={{ gridColumn: "1 / -1" }}>Se incarca studentii...</p>
               )}

               {!loadingAll && errorAll && (
                  <p style={{ gridColumn: "1 / -1", color: "red" }}>
                     {String(errorAll)}
                  </p>
               )}

               {!loadingAll &&
                  !errorAll &&
                  visibleStudents.map((student, idx) => {
                     const key = String(
                        student?.id ??
                           student?.phone ??
                           student?.email ??
                           `__idx_${idx}`,
                     );
                     const color =
                        avatarColorByKey.get(key) || getRandomAvatarColor();

                     return (
                        <StudentItem
                           key={student?.id ?? key}
                           student={student}
                           color={color}
                           initials={getInitials(student)}
                           onOpen={handleOpenStudentPopup}
                           highlightQuery={search.query}
                           secondaryText={student?.phone || student?.email || "–"}
                           highlightClassName="studentItem__highlight"
                        />
                     );
                  })}

               {!loadingAll && !errorAll && filtered.length === 0 && (
                  <p className="studentsUI__empty" style={{ gridColumn: "1 / -1" }}>
                     Nu exista studenti (sau nu se potrivesc filtrului).
                  </p>
               )}

               {!loadingAll && !errorAll && filtered.length > 0 && (
                  <div className="studentsUI__footer">
                     <span className="studentsUI__counter">
                        Afisate {visibleStudents.length} din {filtered.length} studenti
                     </span>

                     {canLoadMore && (
                        <button
                           type="button"
                           className="studentsUI__loadMore"
                           onClick={() =>
                              setVisibleCount((prev) =>
                                 Math.min(filtered.length, prev + batchSize),
                              )
                           }
                        >
                           Afiseaza inca {nextBatch}
                        </button>
                     )}
                  </div>
               )}
            </div>
         </div>
      </div>
   );
}
